import React, { useState } from 'react';
import { ChevronRight, Truck, Store, Clock, Star, CheckCircle, Copy, XCircle, Check } from 'lucide-react';
import { toast } from 'sonner';
import SimpleAddressForm from '../SimpleAddressForm';
import NeighborhoodSelector from './NeighborhoodSelector';
import { applyShippingPromo, FreeShippingProgress } from './shipping';

// CheckoutScreen (tela de finalizacao do pedido do cardapio publico).
// Extraido VERBATIM de PublicMenu.js (Fase 2 refactor). Renderizado como
// <CheckoutScreen .../> recebendo estado/handlers de closure via props.
// Contem o StepIndicator aninhado. Regra de ouro: zero mudanca de comportamento.
const CheckoutScreen = ({
  address,
  addressDeliveryResult,
  appliedCoupon,
  calculatedFee,
  cashbackAmount,
  cashbackData,
  cepRef,
  changeAmount,
  changeAmountRef,
  checkoutScrollTopRef,
  checkoutStep,
  clearAddressData,
  complementRef,
  couponCode,
  couponDiscount,
  couponError,
  couponLoading,
  customerName,
  customerNameRef,
  customerPhone,
  customerPhoneInput,
  customerPhoneRef,
  deliveryConfig,
  deliveryType,
  deliveryZones,
  fetchAddressByCep,
  getCartTotal,
  handleWhatsAppOrder,
  hasActiveCoupons,
  initialAddressForForm,
  isScheduledOrder,
  isSubmittingOrder,
  loadCashbackData,
  loadOrderHistory,
  loadSchedulingSlots,
  loadingCep,
  needsChange,
  neighborhoodRef,
  numberRef,
  paymentMethod,
  pixCopied,
  removeCoupon,
  restaurant,
  saveAddressData,
  saveCustomerData,
  schedulingModalShown,
  schedulingSlots,
  selectedScheduleDate,
  selectedScheduleTime,
  selectedZone,
  setAddress,
  setAddressDeliveryResult,
  setCalculatedFee,
  setCashbackAmount,
  setChangeAmount,
  setCheckoutStep,
  setCouponCode,
  setCouponError,
  setCustomerName,
  setCustomerPhone,
  setCustomerPhoneInput,
  setDeliveryType,
  setFeeError,
  setNeedsChange,
  setPaymentMethod,
  setPixCopied,
  setSchedulingModalShown,
  setSelectedZone,
  setShowClosedStoreModal,
  setShowCouponInput,
  setShowMinOrderModal,
  setShowSchedulingModal,
  setUseCashback,
  showCouponInput,
  streetRef,
  useCashback,
  validateCoupon,
}) => {
    const [checkoutErrors, setCheckoutErrors] = useState({});
    const scrollContainerRef = React.useRef(null);

    // Restaura a posição de scroll a cada (re)mount, ANTES da pintura → sem "pulo" pro topo
    React.useLayoutEffect(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = checkoutScrollTopRef.current;
      }
    });
    
    const subtotal = getCartTotal();
    // Taxa de entrega BASE (sem promoção): modo radius → original_fee ou fee do calculatedFee, modo neighborhood → selectedZone
    const baseDeliveryFee = deliveryType === 'delivery'
      ? (deliveryConfig.mode === 'radius'
          ? (calculatedFee?.original_fee ?? calculatedFee?.fee ?? 0)
          : (selectedZone?.fee ?? 0))
      : 0;
    // Aplicar promoção de frete progressiva (client-side, mesma lógica do backend)
    const shippingPromoObj = deliveryConfig?.shipping_promo;
    const promoInfo = applyShippingPromo(baseDeliveryFee, subtotal, shippingPromoObj, deliveryType);
    const deliveryFee = promoInfo.finalFee;
    const originalDeliveryFee = promoInfo.originalFee;
    const shippingPromoSavings = promoInfo.savings;
    const cashbackDiscount = useCashback ? Math.min(cashbackAmount, subtotal + deliveryFee) : 0;
    const couponDiscountAmount = appliedCoupon ? couponDiscount : 0;
    const total = subtotal + deliveryFee - cashbackDiscount - couponDiscountAmount;
    const totalSavings = cashbackDiscount + couponDiscountAmount + shippingPromoSavings;

    // Auto-load cashback when entering checkout
    React.useEffect(() => {
      if (customerPhone && !cashbackData && restaurant) {
        loadCashbackData(customerPhone);
      }
    }, [customerPhone, cashbackData, restaurant]);

    // 📅 Show scheduling modal when store is closed and scheduling is enabled
    // Uses ref to track if already shown to prevent flickering
    const schedulingCheckedRef = React.useRef(false);
    
    React.useEffect(() => {
      // Prevent multiple executions
      if (schedulingCheckedRef.current || isScheduledOrder || schedulingModalShown) {
        return;
      }
      
      const checkScheduling = async () => {
        const isStoreClosed = restaurant?.status?.is_auto && !restaurant?.status?.is_open;
        const schedulingEnabled = restaurant?.scheduling?.enabled;
        const schedulingAvailable = restaurant?.scheduling?.available;
        
        // Show modal if: store is closed, scheduling is enabled, and not already scheduled
        if (isStoreClosed && schedulingEnabled && schedulingAvailable) {
          schedulingCheckedRef.current = true;
          const slotsData = await loadSchedulingSlots();
          if (slotsData.scheduling_available && slotsData.slots?.length > 0) {
            setSchedulingModalShown(true);
            setShowSchedulingModal(true);
          }
        }
      };
      
      // Run only once when checkout screen mounts
      checkScheduling();
    }, [restaurant, isScheduledOrder, schedulingModalShown]); // Dependencies to control re-runs

    const StepIndicator = () => (
      <div className="flex items-center justify-center space-x-4 mb-6">
        {[1, 2, 3, 4].map(step => (
          <React.Fragment key={step}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              checkoutStep >= step ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              {step}
            </div>
            {step < 4 && (
              <div className={`w-8 h-0.5 ${checkoutStep > step ? 'bg-primary' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );

    const validateStep = () => {
      const errors = {};
      
      if (checkoutStep === 1) {
        // Validar nome e telefone
        const name = customerNameRef.current?.value || customerName;
        const phone = customerPhoneRef.current?.value || customerPhone;
        
        if (!name || name.trim() === '') {
          errors.customerName = 'Por favor, informe seu nome';
        }
        if (!phone || phone.trim() === '' || phone.replace(/\D/g, '').length < 10) {
          errors.customerPhone = 'Por favor, informe um telefone válido';
        }
      } else if (checkoutStep === 3 && deliveryType === 'delivery') {
        // Validar endereço
        const street = streetRef.current?.value || address.street;
        const number = numberRef.current?.value || address.number;
        
        if (!street || street.trim() === '') {
          errors.street = 'Por favor, informe a rua';
        }
        if (!number || number.trim() === '') {
          errors.number = 'Por favor, informe o número';
        }
        
        // ===== VALIDAÇÃO OBRIGATÓRIA DO BAIRRO =====
        if (!address.neighborhood || address.neighborhood.trim() === '') {
          errors.neighborhood = 'Por favor, informe o bairro';
        }
        
        // Validação específica por modo de entrega
        if (deliveryConfig.mode === 'neighborhood') {
          // Modo bairro: precisa ter zonas cadastradas e bairro válido
          if (deliveryZones.length === 0) {
            errors.neighborhood = 'Não há bairros disponíveis para entrega';
          }
          if (address.neighborhood && deliveryZones.length > 0) {
            // Fonte de verdade: selectedZone. Se está setado e existe nas zonas atuais, é válido.
            const selectedStillValid = selectedZone && deliveryZones.some(z => z.id === selectedZone.id);
            if (!selectedStillValid) {
              // Fallback: comparação normalizada (tolera case/espaços)
              const target = String(address.neighborhood).trim().toLowerCase();
              const validZone = deliveryZones.find(z => (z.name || '').trim().toLowerCase() === target);
              if (!validZone) {
                errors.neighborhood = 'Bairro selecionado não é válido';
              } else {
                // Auto-corrigir: sincronizar estado com a zona encontrada
                setSelectedZone(validZone);
                setAddress(prev => ({ ...prev, neighborhood: validZone.name }));
                try {
                  localStorage.setItem('customer_neighborhood', validZone.name);
                  localStorage.setItem('customer_neighborhood_zone_id', validZone.id);
                } catch (_) {}
              }
            }
          }
        } else if (deliveryConfig.mode === 'radius') {
          // Modo raio: precisa ter calculado a taxa OU ter endereço com coordenadas
          if (!calculatedFee && (!address.lat || !address.lng)) {
            errors.neighborhood = 'Selecione um endereço da lista de sugestões para calcular a taxa';
          } else if (calculatedFee === null && address.lat && address.lng) {
            // Tem coordenadas mas taxa ainda não calculou - aguardar
            errors.neighborhood = 'Aguarde o cálculo da taxa de entrega...';
          }
        }
      }
      
      setCheckoutErrors(errors);
      
      // Se houver erros, mostrar toast
      if (Object.keys(errors).length > 0 && errors.neighborhood) {
        toast.error(errors.neighborhood);
      }
      
      return Object.keys(errors).length === 0;
    };

    const handleContinue = async () => {
      // Check if store is suspended/expired before allowing to proceed
      const isSuspended = restaurant?.status?.is_suspended === true;
      const isExpired = restaurant?.status?.is_expired === true;
      const isStoreClosed = restaurant?.status?.is_auto && !restaurant.status.is_open;
      const schedulingEnabled = restaurant?.scheduling?.enabled;
      
      // If store is closed and scheduling is enabled, check for scheduling
      if (isStoreClosed && schedulingEnabled && !isScheduledOrder && !schedulingModalShown && checkoutStep === 4) {
        const slotsData = await loadSchedulingSlots();
        if (slotsData.scheduling_available) {
          setSchedulingModalShown(true);
          setShowSchedulingModal(true);
          return;
        }
      }
      
      if ((isSuspended || isExpired || (isStoreClosed && !isScheduledOrder && !schedulingEnabled)) && checkoutStep === 4) {
        setShowClosedStoreModal(true);
        return;
      }
      
      // Validar valor mínimo quando está na etapa 2 e escolheu delivery
      if (checkoutStep === 2 && deliveryType === 'delivery') {
        const minimumOrder = restaurant?.minimum_order || 0;
        const cartTotal = getCartTotal();
        
        if (minimumOrder > 0 && cartTotal < minimumOrder) {
          setShowMinOrderModal(true);
          return;
        }
      }
      
      if (validateStep()) {
        if (checkoutStep < 4) {
          // Nova etapa começa no topo
          checkoutScrollTopRef.current = 0;
          // Verificar modalidades habilitadas
          const deliveryEnabled = restaurant?.delivery_enabled !== false;
          const pickupEnabled = restaurant?.pickup_enabled !== false;
          const onlyOneMode = (deliveryEnabled && !pickupEnabled) || (!deliveryEnabled && pickupEnabled);
          
          // Se estiver na etapa 1 (dados do cliente) e só uma modalidade está habilitada
          if (checkoutStep === 1 && onlyOneMode) {
            if (deliveryEnabled && !pickupEnabled) {
              // Só delivery - pular para endereço (etapa 3)
              setCheckoutStep(3);
            } else if (!deliveryEnabled && pickupEnabled) {
              // Só retirada - pular para pagamento (etapa 4)
              setCheckoutStep(4);
            }
          }
          // Se estiver na etapa 2 (tipo de entrega) e escolheu "pickup" (retirada no local)
          // pula direto para a etapa 4 (forma de pagamento)
          else if (checkoutStep === 2 && deliveryType === 'pickup') {
            setCheckoutStep(4);
          } else {
            setCheckoutStep(checkoutStep + 1);
          }
          setCheckoutErrors({}); // Limpar erros ao avançar
        } else {
          handleWhatsAppOrder();
        }
      } else {
        // Scroll para o primeiro campo com erro
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    const copyPixKey = () => {
      // Usar a chave PIX cadastrada pelo lojista ou mostrar mensagem de erro
      const pixKey = restaurant?.payment_methods?.pix_key;
      
      if (!pixKey) {
        alert('Chave PIX não configurada pelo estabelecimento. Entre em contato com o restaurante.');
        return;
      }
      
      navigator.clipboard.writeText(pixKey).then(() => {
        setPixCopied(true);
        setTimeout(() => setPixCopied(false), 4000); // Reset após 4 segundos
      }).catch(err => {
        console.error('Erro ao copiar:', err);
        alert('Chave PIX: ' + pixKey);
      });
    };

    return (
      <div
        ref={scrollContainerRef}
        onScroll={(e) => { checkoutScrollTopRef.current = e.currentTarget.scrollTop; }}
        className="fixed inset-0 bg-gray-50 overflow-y-auto"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div className="pt-20 pb-32 px-4">
        <StepIndicator />

        {checkoutStep === 1 && (
          <div>
            <h3 className="text-lg font-bold mb-4">Seus Dados</h3>
            <div className="space-y-4">
              <div>
                <input 
                  ref={customerNameRef}
                  type="text"
                  placeholder="Seu nome completo"
                  defaultValue={customerName}
                  onChange={(e) => {
                    // Limpa o aviso em tempo real assim que o nome fica válido
                    if (checkoutErrors.customerName && e.target.value.trim() !== '') {
                      setCheckoutErrors(prev => {
                        const next = { ...prev };
                        delete next.customerName;
                        return next;
                      });
                    }
                  }}
                  onBlur={(e) => {
                    setCustomerName(e.target.value);
                    saveCustomerData(e.target.value, customerPhoneInput);
                  }}
                  className={`w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-primary focus:border-transparent ${
                    checkoutErrors.customerName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  data-testid="customer-name-input"
                />
                {checkoutErrors.customerName && (
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-yellow-500">⚠️</span>
                    <p className="text-sm text-red-600">{checkoutErrors.customerName}</p>
                  </div>
                )}
              </div>
              <div>
                <input 
                  ref={customerPhoneRef}
                  type="tel"
                  placeholder="(00) 00000-0000"
                  defaultValue={customerPhoneInput}
                  onChange={(e) => {
                    // Limpa o aviso em tempo real assim que o telefone fica válido
                    const digits = e.target.value.replace(/\D/g, '');
                    if (checkoutErrors.customerPhone && digits.length >= 10) {
                      setCheckoutErrors(prev => {
                        const next = { ...prev };
                        delete next.customerPhone;
                        return next;
                      });
                    }
                  }}
                  onBlur={(e) => {
                    const phone = e.target.value;
                    setCustomerPhoneInput(phone);
                    setCustomerPhone(phone);
                    saveCustomerData(customerName, phone);
                    if (phone && phone.replace(/\D/g, '').length >= 10) {
                      loadOrderHistory(phone);
                    }
                  }}
                  className={`w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-primary focus:border-transparent ${
                    checkoutErrors.customerPhone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  data-testid="customer-phone-input"
                />
                {checkoutErrors.customerPhone && (
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-yellow-500">⚠️</span>
                    <p className="text-sm text-red-600">{checkoutErrors.customerPhone}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {checkoutStep === 2 && (
          <div>
            <h3 className="text-lg font-bold mb-4">Tipo de Entrega</h3>
            <div className="space-y-4">
              {/* Mostrar botão Delivery apenas se habilitado */}
              {restaurant?.delivery_enabled !== false && (
                <button
                  onClick={() => setDeliveryType('delivery')}
                  className={`w-full p-4 rounded-lg border-2 flex items-center space-x-3 transition-all duration-300 ${
                    deliveryType === 'delivery' ? 'border-primary bg-primary-light' : 'border-gray-200'
                  }`}
                >
                  <Truck size={24} className={deliveryType === 'delivery' ? 'text-primary' : 'text-gray-600'} />
                  <span className={`font-medium ${deliveryType === 'delivery' ? 'text-primary' : 'text-gray-600'}`}>
                    🛵 Delivery
                  </span>
                </button>
              )}
              {/* Mostrar botão Retirada apenas se habilitado */}
              {restaurant?.pickup_enabled !== false && (
                <button
                  onClick={() => setDeliveryType('pickup')}
                  className={`w-full p-4 rounded-lg border-2 flex items-center space-x-3 transition-all duration-300 ${
                    deliveryType === 'pickup' ? 'border-primary bg-primary-light' : 'border-gray-200'
                  }`}
                >
                  <Store size={24} className={deliveryType === 'pickup' ? 'text-primary' : 'text-gray-600'} />
                  <span className={`font-medium ${deliveryType === 'pickup' ? 'text-primary' : 'text-gray-600'}`}>
                    🏪 Retirada no Local
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {checkoutStep === 3 && deliveryType === 'delivery' && (
          <div>
            <h3 className="text-lg font-bold mb-4">Endereço de Entrega</h3>
            <div className="space-y-4">
              
              {/* MODO NEIGHBORHOOD: Seleção de bairro com taxa fixa */}
              {deliveryConfig.mode === 'neighborhood' && (
                <NeighborhoodSelector
                  zones={deliveryZones}
                  selectedZone={selectedZone}
                  onSelect={(zone) => {
                    const zoneName = zone?.name || '';
                    const zoneId = zone?.id || '';
                    const newAddress = {...address, neighborhood: zoneName};
                    setAddress(newAddress);
                    setSelectedZone(zone);
                    // Limpa o aviso de bairro assim que uma zona válida é selecionada
                    if (zoneName) {
                      setCheckoutErrors(prev => {
                        const next = { ...prev };
                        delete next.neighborhood;
                        return next;
                      });
                    }
                    // Salvar nome E ID do bairro para restaurar corretamente
                    saveAddressData({ 
                      neighborhood: zoneName,
                      neighborhoodZoneId: zoneId
                    });
                  }}
                  error={checkoutErrors.neighborhood}
                />
              )}
              
              {/* MODO RADIUS: OpenStreetMap + OSRM (Gratuito) */}
              {deliveryConfig.mode === 'radius' && (
                <>
                  <SimpleAddressForm
                    key={`address-form-${restaurant?.id}`}
                    restaurantId={restaurant?.id}
                    primaryColor={getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim() || '#3B82F6'}
                    initialAddress={initialAddressForForm}
                    externalDeliveryResult={addressDeliveryResult}
                    onDeliveryResultChange={setAddressDeliveryResult}
                    onAddressComplete={(completeAddress) => {
                      console.log('📝 [PublicMenu] Endereço completo:', completeAddress);
                      // Atualizar estado do endereço - limpar lat/lng antigos se novo endereço não tiver
                      setAddress(prev => ({
                        ...prev,
                        cep: completeAddress.cep,
                        street: completeAddress.street,
                        number: completeAddress.number,
                        complement: completeAddress.complement,
                        neighborhood: completeAddress.neighborhood,
                        city: completeAddress.city,
                        state: completeAddress.state,
                        lat: completeAddress.lat || null,
                        lng: completeAddress.lng || null
                      }));
                      saveAddressData(completeAddress);
                    }}
                    onDeliveryFeeCalculated={(info) => {
                      console.log('💰 [PublicMenu] Taxa calculada:', info);
                      // Atualizar taxa calculada (chamado apenas uma vez pelo novo componente)
                      if (info.withinRange) {
                        setCalculatedFee({
                          fee: info.fee,
                          distance: info.distance,
                          duration: info.duration
                        });
                        setFeeError(null);
                      } else {
                        setCalculatedFee(null);
                        setFeeError(info.message || 'Endereço fora da área de entrega');
                      }
                    }}
                    onAddressReset={clearAddressData}
                  />
                  
                  {/* Nota: O SimpleAddressForm agora exibe seu próprio feedback de taxa */}
                </>
              )}
              
              {/* MODO LEGACY/FALLBACK: Campo CEP antigo (manter para compatibilidade) */}
              {deliveryConfig.mode === 'cep' && (
                <>
                  {/* Campo CEP com busca automática */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">CEP</label>
                    <div className="relative">
                      <input 
                        ref={cepRef}
                        type="text"
                        inputMode="numeric"
                        maxLength={9}
                        defaultValue={address.cep}
                        onChange={(e) => {
                          // Formatar CEP: 00000-000
                          let value = e.target.value.replace(/\D/g, '');
                          if (value.length > 5) {
                            value = value.slice(0, 5) + '-' + value.slice(5, 8);
                          }
                          e.target.value = value;
                          
                          // Buscar automaticamente quando tiver 8 dígitos
                          if (value.replace(/\D/g, '').length === 8) {
                            fetchAddressByCep(value);
                          }
                        }}
                        onBlur={(e) => {
                          const cepValue = e.target.value.replace(/\D/g, '');
                          setAddress(prev => ({...prev, cep: cepValue}));
                          saveAddressData({ cep: cepValue });
                        }}
                        placeholder="00000-000"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                      {loadingCep && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <svg className="animate-spin h-5 w-5 text-primary" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                          </svg>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Digite o CEP para preencher o endereço automaticamente</p>
                  </div>

                  {/* Campo Bairro */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bairro</label>
                    <input 
                      ref={neighborhoodRef}
                      type="text"
                      defaultValue={address.neighborhood}
                      onChange={(e) => {
                        if (checkoutErrors.neighborhood && e.target.value.trim() !== '') {
                          setCheckoutErrors(prev => {
                            const next = { ...prev };
                            delete next.neighborhood;
                            return next;
                          });
                        }
                      }}
                      onBlur={(e) => {
                        const newNeighborhood = e.target.value;
                        setAddress(prev => ({...prev, neighborhood: newNeighborhood}));
                        saveAddressData({ neighborhood: newNeighborhood });
                      }}
                      placeholder="Digite o nome do seu bairro"
                      className={`w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-primary focus:border-transparent ${
                        checkoutErrors.neighborhood ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {checkoutErrors.neighborhood && (
                      <div className="flex items-center gap-1 mt-2">
                        <span className="text-yellow-500">⚠️</span>
                        <p className="text-sm text-red-600">{checkoutErrors.neighborhood}</p>
                      </div>
                    )}
                  </div>
                </>
              )}
              
              {/* Campos comuns: Rua, Número, Complemento - NÃO mostrar no modo radius (já tem seus campos) */}
              {deliveryConfig.mode !== 'radius' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rua</label>
                    <input 
                      ref={streetRef}
                      type="text"
                      defaultValue={address.street}
                      onChange={(e) => {
                        if (checkoutErrors.street && e.target.value.trim() !== '') {
                          setCheckoutErrors(prev => {
                            const next = { ...prev };
                            delete next.street;
                            return next;
                          });
                        }
                      }}
                      onBlur={(e) => {
                        const newStreet = e.target.value;
                        setAddress(prev => ({...prev, street: newStreet}));
                        saveAddressData({ street: newStreet });
                      }}
                      className={`w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-primary focus:border-transparent ${
                        checkoutErrors.street ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {checkoutErrors.street && (
                      <p className="text-sm text-red-600 mt-1">{checkoutErrors.street}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Número</label>
                      <input 
                        ref={numberRef}
                        type="text"
                        defaultValue={address.number}
                        onChange={(e) => {
                          if (checkoutErrors.number && e.target.value.trim() !== '') {
                            setCheckoutErrors(prev => {
                              const next = { ...prev };
                              delete next.number;
                              return next;
                            });
                          }
                        }}
                        onBlur={(e) => {
                          const newNumber = e.target.value;
                          setAddress(prev => ({...prev, number: newNumber}));
                          saveAddressData({ number: newNumber });
                        }}
                        className={`w-full p-3 border rounded-lg transition-colors duration-200 ${
                          checkoutErrors.number ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {checkoutErrors.number && (
                        <p className="text-sm text-red-600 mt-1">{checkoutErrors.number}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Complemento</label>
                      <input 
                        ref={complementRef}
                        type="text"
                        defaultValue={address.complement}
                        onBlur={(e) => {
                          const newComplement = e.target.value;
                          setAddress(prev => ({...prev, complement: newComplement}));
                          saveAddressData({ complement: newComplement });
                        }}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                </>
              )}
              
            </div>
          </div>
        )}

        {checkoutStep === 4 && (
          <div>
            <h3 className="text-lg font-bold mb-4">Pagamento</h3>
            
            {/* Scheduled Order Indicator */}
            {isScheduledOrder && selectedScheduleDate && selectedScheduleTime && (
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                    <Clock className="text-white" size={24} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900">📅 Pedido Agendado</p>
                    <p className="text-base font-bold text-blue-700">
                      {schedulingSlots.find(s => s.date === selectedScheduleDate)?.is_today ? 'Hoje' : 
                       schedulingSlots.find(s => s.date === selectedScheduleDate)?.is_tomorrow ? 'Amanhã' :
                       schedulingSlots.find(s => s.date === selectedScheduleDate)?.day_name} às {selectedScheduleTime}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      await loadSchedulingSlots();
                      setShowSchedulingModal(true);
                    }}
                    className="text-blue-600 text-sm font-medium hover:text-blue-800"
                  >
                    Alterar
                  </button>
                </div>
              </div>
            )}
            
            {/* Cashback Button - Only show if has balance */}
            {cashbackData && 
             cashbackData.enabled === true && 
             typeof cashbackData.available_balance === 'number' &&
             cashbackData.available_balance > 0 && (
              <div className="mb-6">
                {!useCashback ? (
                  <button
                    onClick={() => {
                      const maxCashback = Math.min(
                        cashbackData.available_balance || 0, 
                        subtotal + deliveryFee
                      );
                      setUseCashback(true);
                      setCashbackAmount(maxCashback);
                    }}
                    className="w-full p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-xl hover:from-amber-100 hover:to-yellow-100 transition-all duration-300 shadow-sm hover:shadow-md"
                    data-testid="activate-cashback-button"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center shadow-md">
                          <Star className="text-white" size={24} fill="white" />
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-semibold text-amber-900">Você tem Cashback!</p>
                          <p className="text-lg font-bold text-amber-700">
                            R$ {(cashbackData.available_balance || 0).toFixed(2)} disponível
                          </p>
                        </div>
                      </div>
                      <div className="text-amber-600">
                        <ChevronRight size={24} />
                      </div>
                    </div>
                    <p className="text-xs text-amber-700 mt-2 text-center">
                      👆 Toque para usar seu cashback neste pedido
                    </p>
                  </button>
                ) : (
                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-md animate-pulse">
                          <CheckCircle className="text-white" size={28} fill="white" />
                        </div>
                        <div className="text-left">
                          <p className="text-base font-bold text-green-900">✨ Cashback Ativado!</p>
                          <p className="text-sm text-green-700">
                            Desconto de R$ {cashbackAmount.toFixed(2)} aplicado
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            {cashbackAmount < cashbackData.available_balance 
                              ? `Você ainda tem R$ ${(cashbackData.available_balance - cashbackAmount).toFixed(2)} disponível`
                              : 'Saldo total utilizado'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setUseCashback(false);
                          setCashbackAmount(0);
                        }}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors shadow-sm"
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Payment Methods */}
            <div className="space-y-4 mb-6">
              {['dinheiro', 'cartao', 'cartao_online', 'pix', 'pix_mp', 'vale_refeicao', 'vale_alimentacao']
                .filter(method => method === 'cartao_online'
                  ? restaurant?.payment_methods?.card_online_enabled
                  : restaurant?.payment_methods?.[method])
                .map(method => {
                  // Função para obter as bandeiras formatadas
                  const getBandeirasLabel = (tipo) => {
                    const bandeirasMap = {
                      visa: 'Visa', mastercard: 'Master', elo: 'Elo', 
                      amex: 'Amex', hipercard: 'Hiper', diners: 'Diners',
                      sodexo: 'Sodexo', alelo: 'Alelo', ticket: 'Ticket',
                      vr: 'VR', ben: 'Ben', greencard: 'Green Card', cooper: 'Cooper'
                    };
                    const bandeiras = restaurant?.payment_methods?.[tipo] || [];
                    if (!bandeiras || bandeiras.length === 0) return null;
                    return bandeiras.map(b => bandeirasMap[b] || b).join(' • ');
                  };

                  const bandeirasCartao = method === 'cartao' ? getBandeirasLabel('cartao_bandeiras') : null;
                  const bandeirasValeRefeicao = method === 'vale_refeicao' ? getBandeirasLabel('vale_refeicao_bandeiras') : null;
                  const bandeirasValeAlimentacao = method === 'vale_alimentacao' ? getBandeirasLabel('vale_alimentacao_bandeiras') : null;
                  const bandeiras = bandeirasCartao || bandeirasValeRefeicao || bandeirasValeAlimentacao;

                  return (
                <button
                  key={method}
                  onClick={() => {
                    setPaymentMethod(method);
                    // Reset states when changing payment method
                    if (method !== 'dinheiro') {
                      setNeedsChange(false);
                      setChangeAmount('');
                    }
                    if (method !== 'pix' && method !== 'pix_mp') {
                      setPixCopied(false);
                    }
                  }}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-300 ${
                    paymentMethod === method ? 'border-primary bg-primary-light' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${paymentMethod === method ? 'text-primary' : 'text-gray-600'}`}>
                      {method === 'dinheiro' ? '💵 Dinheiro' : 
                       method === 'cartao' ? '💳 Cartão na Entrega' : 
                       method === 'cartao_online' ? '💳 Cartão Online (Crédito/Débito)' :
                       method === 'pix' ? '🔑 PIX (Chave Manual)' : 
                       method === 'pix_mp' ? '🚀 PIX Automático (Mercado Pago)' :
                       method === 'vale_refeicao' ? '🍽️ Vale Refeição' :
                       '🛒 Vale Alimentação'}
                    </span>
                    {method === 'pix_mp' && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold">
                        Instantâneo
                      </span>
                    )}
                    {method === 'cartao_online' && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-semibold">
                        Pague Agora
                      </span>
                    )}
                  </div>
                  {/* Bandeiras aceitas - exibição discreta */}
                  {bandeiras && (
                    <p className="text-xs text-gray-400 mt-1">
                      {bandeiras}
                    </p>
                  )}
                  {method === 'pix_mp' && (
                    <p className="text-xs text-gray-500 mt-1">
                      QR Code gerado automaticamente - Confirmação instantânea
                    </p>
                  )}
                  {method === 'cartao_online' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Aprovação imediata - Sem precisar maquininha
                    </p>
                  )}
                </button>
                  );
              })}
            </div>

            {/* Campo de Troco - Compacto e harmonioso */}
            {paymentMethod === 'dinheiro' && (
              <div className="mb-4 bg-white border border-gray-200 rounded-lg p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={needsChange}
                    onChange={(e) => {
                      setNeedsChange(e.target.checked);
                      if (!e.target.checked) setChangeAmount('');
                    }}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    💵 Precisa de troco?
                  </span>
                </label>
                
                {needsChange && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <label className="block text-xs text-gray-600 mb-1.5">
                      Troco para quanto?
                    </label>
                    <input
                      ref={changeAmountRef}
                      type="number"
                      step="0.01"
                      defaultValue={changeAmount}
                      onChange={(e) => setChangeAmount(e.target.value)}
                      placeholder="Ex: 50.00"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Botão de Copiar PIX - Aparece ABAIXO das opções quando seleciona PIX */}
            {paymentMethod === 'pix' && (
              <div className="mb-6 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">📱</span>
                  <p className="text-sm text-gray-700 font-medium">
                    Chave PIX para pagamento:
                  </p>
                </div>
                
                {restaurant?.payment_methods?.pix_key ? (
                  <>
                    {pixCopied ? (
                      // Estado: Chave Copiada (Verde)
                      <button className="w-full bg-green-500 text-white rounded-lg p-4 flex items-center justify-center gap-2 font-semibold text-base transition-all">
                        <CheckCircle size={24} />
                        Chave copiada!
                      </button>
                    ) : (
                      // Estado: Normal (Branco com chave)
                      <button
                        onClick={copyPixKey}
                        className="w-full bg-white rounded-lg border border-green-200 p-3 flex items-center justify-between hover:border-green-300 transition-colors"
                      >
                        <span className="text-gray-900 font-medium">{restaurant.payment_methods.pix_key}</span>
                        <Copy size={20} className="text-gray-600" />
                      </button>
                    )}
                    
                    <div className="flex items-center gap-1 mt-2 justify-center">
                      <span className="text-lg">👉</span>
                      <p className="text-xs text-gray-600">
                        {pixCopied ? 'Chave copiada!' : 'Toque para copiar a chave'}
                      </p>
                    </div>
                  </>
                ) : (
                  // Chave PIX não configurada
                  <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-3 text-center">
                    <p className="text-yellow-800 text-sm font-medium">
                      ⚠️ Chave PIX não configurada
                    </p>
                    <p className="text-yellow-700 text-xs mt-1">
                      Entre em contato com o estabelecimento para obter a chave PIX.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* 🎟️ Coupon Section - Compact version with checkbox */}
            {hasActiveCoupons && !appliedCoupon && (
              <div className="mb-4 bg-white border border-gray-200 rounded-lg p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showCouponInput}
                    onChange={(e) => {
                      setShowCouponInput(e.target.checked);
                      if (!e.target.checked) {
                        setCouponCode('');
                        setCouponError('');
                      }
                    }}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700 flex items-center gap-1">
                    🎟️ Tenho um cupom de desconto
                  </span>
                </label>
                
                {showCouponInput && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && couponCode.trim()) {
                            validateCoupon();
                          }
                        }}
                        placeholder="Digite o código"
                        className="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 uppercase focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                        maxLength={30}
                        disabled={couponLoading}
                      />
                      <button
                        onClick={validateCoupon}
                        disabled={!couponCode.trim() || couponLoading}
                        className="px-4 py-2 text-sm bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        {couponLoading ? (
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span className="hidden sm:inline text-xs">Validando</span>
                          </div>
                        ) : (
                          'Aplicar'
                        )}
                      </button>
                    </div>
                    
                    {couponError && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                        <span className="text-red-600 text-xs">{couponError}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Applied Coupon Display */}
            {appliedCoupon && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <p className="text-green-700 font-bold text-sm">{appliedCoupon.code}</p>
                    </div>
                    <p className="text-green-600 text-xs">
                      Desconto de <span className="font-bold">R$ {couponDiscount.toFixed(2)}</span> aplicado! 🎉
                    </p>
                    {appliedCoupon.description && (
                      <p className="text-gray-600 text-xs mt-1">{appliedCoupon.description}</p>
                    )}
                  </div>
                  <button
                    onClick={removeCoupon}
                    className="text-gray-400 hover:text-red-500 transition-colors p-1"
                    title="Remover cupom"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Order Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <FreeShippingProgress
                subtotal={subtotal}
                shippingPromo={shippingPromoObj}
                deliveryType={deliveryType}
                baseFee={baseDeliveryFee}
              />
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>R$ {subtotal.toFixed(2)}</span>
              </div>
              {deliveryType === 'delivery' && (
                <div className="flex justify-between">
                  <span>Taxa de entrega</span>
                  {shippingPromoSavings > 0 ? (
                    <span data-testid="delivery-fee-promo">
                      <span className="line-through text-gray-400 mr-2">R$ {originalDeliveryFee.toFixed(2)}</span>
                      <span className="text-green-600 font-bold">
                        {deliveryFee === 0 ? 'GRÁTIS' : `R$ ${deliveryFee.toFixed(2)}`}
                      </span>
                    </span>
                  ) : (
                    <span>R$ {deliveryFee.toFixed(2)}</span>
                  )}
                </div>
              )}
              {shippingPromoSavings > 0 && promoInfo.activeTier?.label && (
                <div className="flex items-center gap-1 text-green-600 text-xs font-medium">
                  <span>🎁</span>
                  <span>{promoInfo.activeTier.label}</span>
                </div>
              )}
              {useCashback && cashbackDiscount > 0 && (
                <div className="flex justify-between text-green-600 font-semibold">
                  <span>💰 Cashback aplicado</span>
                  <span>- R$ {cashbackDiscount.toFixed(2)}</span>
                </div>
              )}
              {appliedCoupon && couponDiscountAmount > 0 && (
                <div className="flex justify-between text-purple-600 font-semibold">
                  <div className="flex items-center gap-1">
                    <span className="text-base">🎟️</span>
                    <span>Cupom {appliedCoupon.code}</span>
                  </div>
                  <span>- R$ {couponDiscountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total</span>
                <span data-testid="final-total">R$ {total.toFixed(2)}</span>
              </div>
              
              {/* Gamificação - Economia Total */}
              {totalSavings > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center mt-2">
                  <p className="text-green-700 font-bold text-sm flex items-center justify-center gap-2">
                    <span className="text-xl">💚</span>
                    Você está economizando R$ {totalSavings.toFixed(2)}!
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="fixed bottom-4 left-4 right-4 z-50 pb-safe">
          <button 
            onClick={handleContinue}
            disabled={
              // Desabilitar se está enviando pedido
              isSubmittingOrder ||
              // Desabilitar se loja fechada/suspensa no step 4
              (checkoutStep === 4 && !isScheduledOrder && (restaurant?.status?.is_suspended || restaurant?.status?.is_expired || (restaurant?.status?.is_auto && !restaurant.status.is_open))) ||
              // Desabilitar se step 3 (endereço) e delivery sem endereço válido
              (checkoutStep === 3 && deliveryType === 'delivery' && (
                // Modo radius: precisa ter calculado a taxa OU resultado de entrega disponível
                deliveryConfig.mode === 'radius' 
                  ? (!(addressDeliveryResult?.available) && !calculatedFee)
                  // Modo neighborhood: precisa ter bairro selecionado com zonas existentes
                  : (!address.neighborhood || address.neighborhood.trim() === '')
              ))
            }
            className={`w-full py-4 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
              isSubmittingOrder ||
              (checkoutStep === 4 && !isScheduledOrder && (restaurant?.status?.is_suspended || restaurant?.status?.is_expired || (restaurant?.status?.is_auto && !restaurant.status.is_open))) ||
              (checkoutStep === 3 && deliveryType === 'delivery' && (
                deliveryConfig.mode === 'radius' 
                  ? (!(addressDeliveryResult?.available) && !calculatedFee)
                  : (!address.neighborhood || address.neighborhood.trim() === '')
              ))
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-primary hover:bg-primary-hover text-white animate-slow-pulse hover:animate-none hover:scale-105'
            }`}
            data-testid="checkout-continue-btn"
          >
            {isSubmittingOrder ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Enviando...
              </>
            ) : (
              checkoutStep < 4 ? 'Continuar' : (isScheduledOrder ? '📅 Finalizar Pedido Agendado' : 'Finalizar Pedido')
            )}
          </button>
        </div>
        </div>
      </div>
    );
  };

export default CheckoutScreen;
